import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { listTemplates, THEMES, templateOf } from "@/lib/templates";
import { AdminTitle } from "@/admin/AdminLayout";
import { FormDialog, Field } from "@/admin/FormDialog";
import { ImageField } from "@/admin/ImageField";

const SEC0 = { name: "", slug: "", template: "generic", theme: "neutral", description: "", icon_image: "", infobox_fields: [], order: 0, show_in_nav: true };

export default function AdminSections() {
  const { sections, refresh } = useSite();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));

  const save = async () => {
    setBusy(true);
    try {
      form.id ? await api.put(`/sections/${form.id}`, form) : await api.post("/sections", form);
      toast.success("Section saved"); setForm(null); refresh();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete section "${s.name}"?`)) return;
    try { await api.delete(`/sections/${s.id}`); toast.success("Deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const changeTemplate = (template) => set({ template, theme: templateOf(template).theme, infobox_fields: templateOf(template).fields });

  return (
    <div data-testid="admin-sections">
      <AdminTitle eyebrow="Chambers of the codex" title="Sections">
        <button onClick={() => setForm({ ...SEC0 })} data-testid="add-section-button" className="btn-illum theme-flame"><Plus className="w-4 h-4" /> Add section</button>
      </AdminTitle>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {sections.map((s) => {
          const Icon = templateOf(s.template).icon;
          return (
            <div key={s.id} data-testid={`admin-section-${s.slug}`} className={`vellum p-5 theme-${s.theme}`}>
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 grid place-items-center bg-[color:var(--accent)] text-white border border-[#c9a227] shrink-0"><Icon className="w-5 h-5" /></span>
                <div className="flex-1">
                  <p className="font-heading font-bold">{s.name}</p>
                  <p className="text-xs text-[#5e4a35]">{templateOf(s.template).label} · {s.article_count} pages · order {s.order}{!s.show_in_nav && " · hidden from nav"}</p>
                </div>
              </div>
              <p className="text-sm mt-3 line-clamp-2">{s.description}</p>
              <div className="flex gap-1 mt-3">
                <Link to={`/admin/articles/new?section=${s.slug}`} className="text-xs font-heading border border-[#c9a227] px-2 py-1 hover:bg-[#c9a227]/20">+ Article</Link>
                <button onClick={() => setForm({ ...s })} data-testid={`edit-section-${s.slug}`} className="p-1.5 hover:bg-[#c9a227]/20"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(s)} data-testid={`delete-section-${s.slug}`} className="p-1.5 text-[#991b1b] hover:bg-[#991b1b]/10"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      <FormDialog open={!!form} onOpenChange={() => setForm(null)} title={form?.id ? "Edit section" : "New section"} onSubmit={save} busy={busy} testid="section-dialog">
        {form && <>
          <Field label="Name"><input required data-testid="section-name-input" value={form.name} onChange={(e) => set({ name: e.target.value })} className="admin-input" placeholder="e.g. Battles & Wars" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Page template">
              <select data-testid="section-template-select" value={form.template} onChange={(e) => changeTemplate(e.target.value)} className="admin-input">
                {listTemplates().map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Colour theme">
              <select data-testid="section-theme-select" value={form.theme} onChange={(e) => set({ theme: e.target.value })} className="admin-input">
                {THEMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description"><textarea rows={2} data-testid="section-description-input" value={form.description} onChange={(e) => set({ description: e.target.value })} className="admin-input" /></Field>
          <Field label="Default infobox fields (one per line)">
            <textarea rows={4} data-testid="section-fields-input" value={form.infobox_fields.join("\n")} onChange={(e) => set({ infobox_fields: e.target.value.split("\n") })} onBlur={() => set({ infobox_fields: form.infobox_fields.map((x) => x.trim()).filter(Boolean) })} className="admin-input" />
          </Field>
          <div className="grid grid-cols-3 gap-4 items-end">
            <Field label="Order"><input type="number" value={form.order} onChange={(e) => set({ order: Number(e.target.value) })} className="admin-input" /></Field>
            <Field label="URL slug"><input value={form.slug} onChange={(e) => set({ slug: e.target.value })} placeholder="auto" className="admin-input" /></Field>
            <label className="flex items-center gap-2 text-sm pb-2"><input type="checkbox" checked={form.show_in_nav} onChange={(e) => set({ show_in_nav: e.target.checked })} className="accent-[#991b1b]" /> Show in menu</label>
          </div>
          <div className="w-40"><ImageField label="Icon / emblem (optional)" value={form.icon_image} onChange={(v) => set({ icon_image: v })} aspect="aspect-square" testid="section-icon-field" /></div>
        </>}
      </FormDialog>
    </div>
  );
}
